import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import {
  ResponsiveContainer, ComposedChart, LineChart, Line, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import api from '../../services/api';
import './GrowthBarometerApp.css';

const SOURCE = 'Source: LSEG Datastream, Drummond Capital Partners';

// Combined "Barometers" tab — Global thick navy, regions thin.
const REGION_COLOUR = { USA_CAI: '#f0a500', EU_CAI: '#6c757d', ASIA_CAI: '#599BE3', AU_CAI: '#91CF9B', Global_CAI: '#072040' };
// Per-region tabs (PowerBI colours): Consumer navy, Housing amber, Industrial grey, Labour blue; barometer line green.
const SECTOR_ORDER = ['Consumer', 'Housing', 'Industrial', 'Labour'];
const SECTOR_COLOUR = { Consumer: '#072040', Housing: '#f0a500', Industrial: '#6c757d', Labour: '#599BE3' };
const CAI_LINE = '#5aa469';
// Combined decomposition tab (deck colours, matches the IC chart): CAI navy line.
const DECK_ORDER = ['Housing', 'Consumer', 'Industrial', 'Labour'];
const DECK_COLOUR = { Housing: '#f0a500', Consumer: '#599BE3', Industrial: '#6c757d', Labour: '#91CF9B' };
const DECK_CAI = '#072040';

const fmt = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
// X-axis tick label: full month + year, e.g. "July 2025" (dates are ISO 'YYYY-MM-DD').
const monthYearTick = (d) => {
  const [y, m] = String(d).split('-');
  return MONTH_NAMES[(+m) - 1] ? `${MONTH_NAMES[(+m) - 1]} ${y}` : y;
};
const yearsAgoISO = (dates, n) => `${Math.max(+dates[0].slice(0, 4), +dates[dates.length - 1].slice(0, 4) - n)}-01-01`;

const EXPORT_FONT_SCALE = 1.5;
// Enlarge the HTML text around the chart (title, source line, legend, cell headings) for the
// PNG. Runs on html2canvas's off-screen clone so the on-screen page is untouched. The SVG
// chart text (axis ticks + labels) is enlarged separately by re-rendering the chart at a
// larger font scale during export — html2canvas does not pick up clone edits to inline SVG.
function bumpFontsForExport(root, factor) {
  const view = root.ownerDocument.defaultView || window;
  root.querySelectorAll('.gb-export-title, .gb-export-source, .gb-lg, .gb-cell h5').forEach((el) => {
    const cur = parseFloat(view.getComputedStyle(el).fontSize);
    if (cur) el.style.fontSize = `${cur * factor}px`;
  });
}

async function downloadPng(el, filename) {
  if (!el) return;
  el.setAttribute('data-gb-export', '1');   // scopes the font bump to this chart in the clone
  try {
    const canvas = await html2canvas(el, {
      scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: true,
      onclone: (doc) => {
        const target = doc.querySelector('[data-gb-export="1"]');
        if (target) bumpFontsForExport(target, EXPORT_FONT_SCALE);
      },
    });
    const link = document.createElement('a');
    link.download = filename.endsWith('.png') ? filename : `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } finally {
    el.removeAttribute('data-gb-export');
  }
}

// Export a chart to PNG at an enlarged font scale: flip `exporting` on so the chart
// re-renders with bigger axis fonts, wait for that frame to paint, then rasterise.
function useChartExport() {
  const ref = useRef(null);
  const [exporting, setExporting] = useState(false);
  const runExport = async (filename) => {
    setExporting(true);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try { await downloadPng(ref.current, filename); }
    finally { setExporting(false); }
  };
  return { ref, exporting, runExport, fontScale: exporting ? EXPORT_FONT_SCALE : 1 };
}

function Legend({ items }) {
  return (
    <div className="gb-inline-legend">
      {items.map(([label, colour, isLine]) => (
        <span key={label} className="gb-lg"><i style={{ background: colour, height: isLine ? 3 : 12 }} /> {label}</span>
      ))}
    </div>
  );
}

// Short axis/column label, e.g. "Jul 25" (dates are ISO 'YYYY-MM-DD').
const shortMon = (d) => {
  const [y, m] = String(d).split('-');
  return MONTH_NAMES[(+m) - 1] ? `${MONTH_NAMES[(+m) - 1].slice(0, 3)} ${y.slice(2)}` : y;
};

// Look-through data table: inputs feeding one country × sector, sorted by |corr| to
// the sector barometer (dominant driver first), last 12 months across the columns.
function SectorTable({ data }) {
  const N = Math.min(12, data.dates.length);
  const months = data.dates.slice(-N);
  return (
    <div className="gb-lt-tablewrap">
      <table className="gb-lt-table">
        <thead>
          <tr>
            <th className="gb-lt-name">Input series</th>
            <th title="Correlation of the input to the sector barometer over full history">Corr</th>
            {months.map((m) => <th key={m}>{shortMon(m)}</th>)}
          </tr>
        </thead>
        <tbody>
          <tr className="gb-lt-sectorrow">
            <td className="gb-lt-name">◆ Sector barometer (output)</td>
            <td>—</td>
            {data.contribution.slice(-N).map((v, i) => <td key={i}>{fmt(v)}</td>)}
          </tr>
          {data.series.map((s) => {
            const strong = s.corr_to_sector !== null && Math.abs(s.corr_to_sector) >= 0.5;
            return (
              <tr key={s.expression}>
                <td className="gb-lt-name" title={s.name || s.expression}>{s.expression}</td>
                <td className={strong ? 'gb-lt-corr strong' : 'gb-lt-corr'}>{fmt(s.corr_to_sector)}</td>
                {s.values.slice(-N).map((v, i) => <td key={i}>{fmt(v)}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SectorLookthroughModal({ drill, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    api.get('/api/growth-barometer/sector-series',
      { params: { country: drill.country, sector: drill.sector, years: 3 } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [drill]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="gb-modal-overlay" onClick={onClose}>
      <div className="gb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gb-modal-head">
          <div>
            <h3>{drill.label} · {drill.sector} — look-through</h3>
            {data && (
              <span className="gb-muted">
                {data.count} inputs · sector barometer latest {fmt(data.sector_latest)} · as at {data.as_of} · sorted by correlation
              </span>
            )}
          </div>
          <button className="gb-modal-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        {err && <div className="gb-alert">{err}</div>}
        {!data && !err && <p className="gb-muted">Loading…</p>}
        {data && <SectorTable data={data} />}
        {data && (
          <p className="gb-lt-note">
            Values are the transformed inputs (Datastream PCH#/ACH#/seasonal-adjustment), last {Math.min(12, data.dates.length)} months.
            A high correlation together with a large recent move flags a single dominant driver.
          </p>
        )}
      </div>
    </div>
  );
}

function DateSlicer({ first, last, from, to, setFrom, setTo }) {
  return (
    <span className="gb-slice">
      <label>Dates</label>
      <input type="date" min={first} max={last} value={from} onChange={(e) => setFrom(e.target.value)} />
      <input type="date" min={first} max={last} value={to} onChange={(e) => setTo(e.target.value)} />
    </span>
  );
}

function YSlicer({ range, yMin, yMax, setYMin, setYMax, step = 0.5 }) {
  return (
    <span className="gb-slice gb-yslice">
      <label>Y-axis</label>
      <input type="range" min={range[0]} max={range[1]} step={step} value={yMin} title="Y min"
             onChange={(e) => setYMin(Math.min(+e.target.value, yMax - step))} />
      <input type="range" min={range[0]} max={range[1]} step={step} value={yMax} title="Y max"
             onChange={(e) => setYMax(Math.max(+e.target.value, yMin + step))} />
      <b>{yMin} … {yMax}</b>
    </span>
  );
}

// A single chart with its own date + Y-axis slicers and a PNG export.
function SlicedChart({ name, title, legend, dates, rows, defaultFrom, yRange = [-8, 8], defaultY = [-7.5, 7.5], children }) {
  const first = dates[0], last = dates[dates.length - 1];
  const [from, setFrom] = useState(defaultFrom || first);
  const [to, setTo] = useState(last);
  const [yMin, setYMin] = useState(defaultY[0]);
  const [yMax, setYMax] = useState(defaultY[1]);
  const { ref, runExport, fontScale } = useChartExport();
  const filtered = rows.filter((r) => r.date >= from && r.date <= to);
  const reset = () => { setFrom(defaultFrom || first); setTo(last); setYMin(defaultY[0]); setYMax(defaultY[1]); };
  return (
    <div className="gb-block">
      <div className="gb-toolbar">
        <div className="gb-slicers">
          <DateSlicer first={first} last={last} from={from} to={to} setFrom={setFrom} setTo={setTo} />
          <YSlicer range={yRange} yMin={yMin} yMax={yMax} setYMin={setYMin} setYMax={setYMax} />
          <button className="gb-btn-sm" onClick={reset}>Reset</button>
        </div>
        <button className="gb-btn-sm gb-export-btn" onClick={() => runExport(name)}>⤓ PNG</button>
      </div>
      <div ref={ref} className="gb-export">
        <div className="gb-export-title">{title}</div>
        {legend}
        {children(filtered, [yMin, yMax], fontScale)}
        <div className="gb-export-source">{SOURCE}</div>
      </div>
    </div>
  );
}

// Combined 2x2 sub-component decomposition (one shared date + Y slicer, one PNG).
function CombinedDecomp({ decomp, lookthrough, onDrill }) {
  const dates = decomp.dates;
  const first = dates[0], last = dates[dates.length - 1];
  const defFrom = yearsAgoISO(dates, 5);
  const [from, setFrom] = useState(defFrom);
  const [to, setTo] = useState(last);
  const [yMin, setYMin] = useState(-7.5);
  const [yMax, setYMax] = useState(7.5);
  const { ref, runExport, fontScale } = useChartExport();
  const rowsFor = (c) => dates.map((d, i) => {
    const row = { date: d, CAI: c.cai[i] };
    DECK_ORDER.forEach((sec) => { row[sec] = c.sectors[sec] ? c.sectors[sec][i] : null; });
    return row;
  }).filter((r) => r.date >= from && r.date <= to);
  const reset = () => { setFrom(defFrom); setTo(last); setYMin(-7.5); setYMax(7.5); };
  return (
    <div className="gb-block">
      <div className="gb-toolbar">
        <div className="gb-slicers">
          <DateSlicer first={first} last={last} from={from} to={to} setFrom={setFrom} setTo={setTo} />
          <YSlicer range={[-10, 10]} yMin={yMin} yMax={yMax} setYMin={setYMin} setYMax={setYMax} />
          <button className="gb-btn-sm" onClick={reset}>Reset</button>
        </div>
        <button className="gb-btn-sm gb-export-btn" onClick={() => runExport('growth-barometers-decomposition')}>⤓ PNG</button>
      </div>
      <div ref={ref} className="gb-export">
        <div className="gb-export-title">Growth Barometers: Sub-Component Decomposition (z-score)</div>
        <div className="gb-grid">
          {decomp.countries.map((c) => (
            <div key={c.key} className="gb-cell">
              <h5>{c.label}</h5>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={rowsFor(c)} margin={{ top: 6, right: 10, left: 0, bottom: 4 }} barCategoryGap="6%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={monthYearTick} tick={{ fontSize: 10 * fontScale }} minTickGap={80} />
                  <YAxis domain={[yMin, yMax]} allowDataOverflow ticks={[-6, -4, -2, 0, 2, 4, 6]} tick={{ fontSize: 10 * fontScale }} width={Math.round(30 * fontScale)} />
                  <ReferenceLine y={0} stroke="#6c757d" />
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => fmt(v)} />
                  {DECK_ORDER.map((sec) => (
                    <Bar key={sec} dataKey={sec} stackId="s" fill={DECK_COLOUR[sec]} isAnimationActive={false}
                         cursor={lookthrough ? 'pointer' : undefined}
                         onClick={lookthrough ? () => onDrill({ country: c.key, sector: sec, label: c.label }) : undefined} />
                  ))}
                  <Line type="monotone" dataKey="CAI" stroke={DECK_CAI} strokeWidth={1.8} dot={false} isAnimationActive={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
        <Legend items={[['CAI (total)', DECK_CAI, true], ...DECK_ORDER.map((s) => [s, DECK_COLOUR[s], false])]} />
        <div className="gb-export-source">{SOURCE}</div>
      </div>
    </div>
  );
}

export default function GrowthBarometerApp() {
  const [series, setSeries] = useState(null);
  const [decomp, setDecomp] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('barometers');
  const [lookthrough, setLookthrough] = useState(false);
  const [drill, setDrill] = useState(null);   // {country, sector, label} — open modal

  useEffect(() => {
    setError(null);
    Promise.all([
      api.get('/api/growth-barometer/series'),
      api.get('/api/growth-barometer/decomposition'),
    ]).then(([s, d]) => { setSeries(s.data); setDecomp(d.data); })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);

  if (error) return <div className="gb-app"><div className="gb-alert">{error}</div></div>;
  if (!series || !decomp) return <div className="gb-app"><p className="gb-muted">Loading…</p></div>;

  const lineRows = series.dates.map((d, i) => {
    const row = { date: d };
    series.series.forEach((s) => { row[s.key] = s.values[i]; });
    return row;
  });
  const decompRowsFor = (c) => decomp.dates.map((d, i) => {
    const row = { date: d, CAI: c.cai[i] };
    SECTOR_ORDER.forEach((sec) => { row[sec] = c.sectors[sec] ? c.sectors[sec][i] : null; });
    return row;
  });
  const from6 = yearsAgoISO(series.dates, 6);
  const from6d = yearsAgoISO(decomp.dates, 6);

  const TABS = [['barometers', 'Barometers'], ['combined', 'Combined'], ...decomp.countries.map((c) => [c.key, c.label])];

  return (
    <div className="gb-app">
      <div className="gb-head">
        <h2>Growth Barometers</h2>
        <span className="gb-muted">as at {series.as_of}</span>
      </div>

      <div className="gb-tabs">
        {TABS.map(([id, label]) => (
          <button key={id} className={`gb-tab${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab !== 'barometers' && (
        <div className="gb-lt-bar">
          <label className="gb-lt-check">
            <input type="checkbox" checked={lookthrough} onChange={(e) => setLookthrough(e.target.checked)} />
            Look-through
          </label>
          <span className="gb-muted">
            {lookthrough ? 'click a sector bar to see its underlying input series' : 'toggle on, then click a sector bar to drill into its inputs'}
          </span>
        </div>
      )}

      {tab === 'barometers' && (
        <SlicedChart name="growth-barometers" title="Growth Barometer" dates={series.dates} rows={lineRows}
                     defaultFrom={from6} yRange={[-8, 8]} defaultY={[-7.5, 7.5]}
                     legend={<Legend items={series.series.map((s) => [s.label, REGION_COLOUR[s.key], true])} />}>
          {(rows, yd, fs) => (
            <ResponsiveContainer width="100%" height={460}>
              <LineChart data={rows} margin={{ top: 10, right: 20, left: 6, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tickFormatter={monthYearTick} tick={{ fontSize: 11 * fs }} minTickGap={90} />
                <YAxis yAxisId="l" domain={yd} allowDataOverflow tick={{ fontSize: 11 * fs }} width={Math.round(60 * fs)}
                       label={{ value: 'Z Score', angle: -90, position: 'insideLeft', fontSize: 12 * fs }} />
                <YAxis yAxisId="r" orientation="right" domain={yd} allowDataOverflow tick={{ fontSize: 11 * fs }} width={Math.round(60 * fs)} />
                <ReferenceLine yAxisId="l" y={0} stroke="#6c757d" />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => fmt(v)} />
                {series.series.map((s) => (
                  <Line yAxisId="l" key={s.key} type="monotone" dataKey={s.key} name={s.label}
                        stroke={REGION_COLOUR[s.key]} strokeWidth={s.key === 'Global_CAI' ? 2.4 : 1} dot={false}
                        isAnimationActive={false} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </SlicedChart>
      )}

      {tab === 'combined' && <CombinedDecomp decomp={decomp} lookthrough={lookthrough} onDrill={setDrill} />}

      {decomp.countries.map((c) => tab === c.key && (
        <React.Fragment key={c.key}>
          <SlicedChart name={`${c.label}-growth-barometer`} title={`${c.label} Growth Barometer`} dates={decomp.dates} rows={decompRowsFor(c)}
                       defaultFrom={from6d} yRange={[-16, 16]} defaultY={[-11, 11]}
                       legend={<Legend items={[...SECTOR_ORDER.map((s) => [s, SECTOR_COLOUR[s], false]), ['Growth Barometer', CAI_LINE, true]]} />}>
            {(rows, yd, fs) => (
              <ResponsiveContainer width="100%" height={380}>
                <ComposedChart data={rows} margin={{ top: 10, right: 20, left: 6, bottom: 6 }} barCategoryGap="6%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={monthYearTick} tick={{ fontSize: 11 * fs }} minTickGap={90} />
                  <YAxis domain={yd} allowDataOverflow tick={{ fontSize: 11 * fs }} width={Math.round(60 * fs)}
                         label={{ value: 'Z Score', angle: -90, position: 'insideLeft', fontSize: 12 * fs }} />
                  <ReferenceLine y={0} stroke="#6c757d" />
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => fmt(v)} />
                  {SECTOR_ORDER.map((sec) => (
                    <Bar key={sec} dataKey={sec} stackId="s" fill={SECTOR_COLOUR[sec]} isAnimationActive={false}
                         cursor={lookthrough ? 'pointer' : undefined}
                         onClick={lookthrough ? () => setDrill({ country: c.key, sector: sec, label: c.label }) : undefined} />
                  ))}
                  <Line type="monotone" dataKey="CAI" name="Growth Barometer" stroke={CAI_LINE} strokeWidth={2.4} dot={false} isAnimationActive={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </SlicedChart>

          <SlicedChart name={`${c.label}-subcomponents`} title={`${c.label} sub-components`} dates={decomp.dates} rows={decompRowsFor(c)}
                       defaultFrom={from6d} yRange={[-8, 8]} defaultY={[-4, 4]}
                       legend={<Legend items={SECTOR_ORDER.map((s) => [s, SECTOR_COLOUR[s], true])} />}>
            {(rows, yd, fs) => (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={rows} margin={{ top: 10, right: 20, left: 6, bottom: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tickFormatter={monthYearTick} tick={{ fontSize: 11 * fs }} minTickGap={90} />
                  <YAxis domain={yd} allowDataOverflow tick={{ fontSize: 11 * fs }} width={Math.round(60 * fs)}
                         label={{ value: 'Z Score', angle: -90, position: 'insideLeft', fontSize: 12 * fs }} />
                  <ReferenceLine y={0} stroke="#6c757d" />
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => fmt(v)} />
                  {SECTOR_ORDER.map((sec) => (
                    <Line key={sec} type="monotone" dataKey={sec} name={sec} stroke={SECTOR_COLOUR[sec]}
                          strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </SlicedChart>
        </React.Fragment>
      ))}

      {drill && <SectorLookthroughModal drill={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}
